import React, { useEffect, useState } from "react";
import "../css/DeviceInfo.css";
import DeviceLayout from "./DeviceLayout";


const DeviceUpdate = () => {
  const [loading, setLoading] = useState(true);
  const [fw, setFw] = useState("");
  const [sfw, setSfw] = useState("");
  const [updating, setUpdating] = useState(false);
  const [status, setStatus] = useState("Idle");
  const [progress, setProgress] = useState(0);
  const [activeJobId, setActiveJobId] = useState(null);
  const [updatePlan, setUpdatePlan] = useState(null);

  const getDeviceIdFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("id");
  };

  const refreshDeviceVersions = async () => {
    const id = getDeviceIdFromUrl();
    if (!id) {
      throw new Error("Device ID not found in the URL.");
    }

    const response = await fetch(`/api/device/desc/${id}`, {
      credentials: "include",
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to fetch device versions");
    }

    setFw(data.fw ?? "");
    setSfw(data.sfw ?? "");
    return data;
  };

  const fetchUpdatePlan = async () => {
    const id = getDeviceIdFromUrl();
    if (!id) {
      return null;
    }

    const response = await fetch(`/api/device/update/plan/${id}`, {
      credentials: "include",
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to fetch update plan");
    }

    setUpdatePlan(data);
    return data;
  };

  const mapJobToUi = (job) => {
    if (!job) {
      return;
    }

    const jobProgress = Number.isFinite(job.progress) ? job.progress : 0;
    setProgress(jobProgress);
    setStatus(job.message || job.stage || "Idle");

    if (job.job_id) {
      setActiveJobId(job.job_id);
    }

    const finished = job.status === "completed" || job.status === "failed";
    setUpdating(!finished && job.status !== "not_found");
  };

  const fetchUpdateStatus = async () => {
    const id = getDeviceIdFromUrl();
    if (!id) {
      return null;
    }

    const response = await fetch(`/api/device/update/status/${id}`, {
      credentials: "include",
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to fetch update status");
    }

    mapJobToUi(data);

    // Refresh displayed fw/sfw after terminal state.
    if (data.status === "completed" || data.status === "failed") {
      await refreshDeviceVersions();
      await fetchUpdatePlan();
    }

    return data;
  };

useEffect(() => {
  const fetchData = async () => {
    try {
      await refreshDeviceVersions();
      await fetchUpdatePlan();
      await fetchUpdateStatus();
    } catch (error) {
      console.error("Error fetching device data:", error);
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, []);

  useEffect(() => {
    if (!updating) {
      return undefined;
    }

    const interval = setInterval(() => {
      fetchUpdateStatus().catch((error) => {
        console.error("Error fetching update status:", error);
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [updating]);

  const handleUpdate = async () => {
    const id = getDeviceIdFromUrl();
    if (!id) {
      alert("Device ID not found in the URL.");
      return;
    }

    setUpdating(true);
    setProgress(5);
    setStatus("Starting update job...");

    try {
      const plan = await fetchUpdatePlan();
      if (!plan?.available) {
        throw new Error("Update is not available for this firmware range.");
      }

      const response = await fetch(`/api/device/update/start/${id}`, {
        method: "POST",
        credentials: "include",
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to start update job.");
      }

      mapJobToUi(data);
      setStatus(data.message || "Update job started");

      // Immediately sync status in case backend advanced quickly.
      await fetchUpdateStatus();

    } catch (err) {
      setStatus(`Update failed: ${err.message}`);
      alert(`Update failed: ${err.message}`);
    }
  };

  return (
    <DeviceLayout>
      {loading ? (
        <div className="spinner-container">
          <div className="spinner"></div> {/* Replace with a spinner component or CSS loader */}
        </div>
      ) : (
        <>
          <div>
            <b>Warning: Updating the BMS will disconnect the contactors. Do not perform an update while current is flowing. </b><p></p>If update fails, check if BMS has good wifi signal. If issues persist, contact support.<p></p>
        <table>
          <thead>
            <tr>
              <th>Current Firmware</th>
              <th>Current Filesystem</th>
              <th>Target Firmware</th>
              <th>Target Filesystem</th>
              <th>Update Action</th>
            </tr>
          </thead>
     <tbody>
            <tr>
              <td>{fw || "-"}</td>
              <td>{sfw || "-"}</td>
              <td>{updatePlan?.available ? updatePlan.firmware_file : "Not available"}</td>
              <td>{updatePlan?.available ? updatePlan.filesystem_file : "Not available"}</td>
              <td>
                <button type="button" onClick={handleUpdate} disabled={updating || !updatePlan?.available}>
                  {updating ? "Update Running..." : "Start Update"}
                </button>
              </td>
            </tr>
     </tbody>
          </table>

          <div style={{ marginTop: "12px" }}>
            <div style={{ marginBottom: "8px", fontWeight: 600 }}>Status: {status}</div>
            {activeJobId ? <div style={{ marginBottom: "8px" }}>Job ID: {activeJobId}</div> : null}
            <progress value={progress} max="100" style={{ width: "100%", height: "20px" }} />
            <div style={{ marginTop: "6px" }}>{progress}%</div>
          </div>

          </div>
        </>
      )}
    </DeviceLayout>
  );
  
};

export default DeviceUpdate;
